import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-slate-800/50 backdrop-blur-md border-b border-slate-700"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center space-x-4">
            <Link
              to="/"
              className="flex items-center space-x-2 text-slate-300 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Sera</span>
            </Link>
          </div>
        </div>
      </motion.header>

      {/* Content */}
      <motion.main
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12"
      >
        <div className="prose prose-invert max-w-none">
          <h1 className="text-4xl font-bold mb-8 bg-gradient-to-r from-teal-400 to-blue-500 bg-clip-text text-transparent">
            Terms of Service
          </h1>
          
          <p className="text-slate-400 text-lg mb-8">
            Last updated: [Date]
          </p>

          <div className="space-y-8">
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">1. Acceptance of Terms</h2>
              <p className="text-slate-300 leading-relaxed">
                By downloading, installing, or using Sera ("the App"), you agree to be bound by these Terms of Service. 
                If you do not agree to these terms, please do not use our service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">2. Description of Service</h2>
              <p className="text-slate-300 leading-relaxed">
                Sera is a notification management application that helps users:
              </p>
              <ul className="list-disc list-inside text-slate-300 mt-4 space-y-2">
                <li>Batch non-urgent notifications for scheduled delivery</li>
                <li>Set custom notification delivery times</li>
                <li>Allow priority apps to break through batching</li>
                <li>Maintain focus by reducing notification interruptions</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">3. User Responsibilities</h2>
              <p className="text-slate-300 leading-relaxed">
                As a user of Sera, you agree to:
              </p>
              <ul className="list-disc list-inside text-slate-300 mt-4 space-y-2">
                <li>Use the app in accordance with applicable laws and regulations</li>
                <li>Not attempt to reverse engineer or modify the app</li>
                <li>Respect the privacy and rights of others</li>
                <li>Provide accurate information when required</li>
                <li>Use the app only on devices you own or have permission to use</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">4. Privacy and Data</h2>
              <p className="text-slate-300 leading-relaxed">
                Your privacy is important to us. Our collection and use of personal information is governed by our 
                Privacy Policy, which is incorporated into these Terms by reference. By using Sera, you consent to 
                the collection and use of information as described in our Privacy Policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">5. Intellectual Property</h2>
              <p className="text-slate-300 leading-relaxed">
                Sera and its original content, features, and functionality are owned by [Company Name] and are 
                protected by international copyright, trademark, patent, trade secret, and other intellectual 
                property or proprietary rights laws.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">6. Disclaimers</h2>
              <p className="text-slate-300 leading-relaxed">
                Sera is provided "as is" without warranties of any kind. We do not guarantee that:
              </p>
              <ul className="list-disc list-inside text-slate-300 mt-4 space-y-2">
                <li>The app will be available at all times</li>
                <li>All notifications will be batched correctly</li>
                <li>The app will work on all devices or operating systems</li>
                <li>Third-party apps will respect our batching settings</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">7. Limitation of Liability</h2>
              <p className="text-slate-300 leading-relaxed">
                To the maximum extent permitted by law, Sera shall not be liable for any indirect, incidental, 
                special, consequential, or punitive damages, including without limitation, loss of profits, data, 
                use, goodwill, or other intangible losses resulting from your use of the app.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">8. Termination</h2>
              <p className="text-slate-300 leading-relaxed">
                We may terminate or suspend your access to Sera immediately, without prior notice or liability, 
                for any reason whatsoever, including without limitation if you breach the Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">9. Changes to Terms</h2>
              <p className="text-slate-300 leading-relaxed">
                We reserve the right to modify or replace these Terms at any time. If a revision is material, 
                we will try to provide at least 30 days notice prior to any new terms taking effect.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">10. Contact Information</h2>
              <p className="text-slate-300 leading-relaxed">
                If you have any questions about these Terms of Service, please contact us at:
              </p>
              <div className="mt-4 p-4 bg-slate-800 rounded-lg">
                <p className="text-slate-300">
                  Email: legal@sera.app<br />
                  Website: https://sera.app/contact
                </p>
              </div>
            </section>
          </div>

          <div className="mt-12 p-6 bg-gradient-to-r from-teal-400/10 to-blue-500/10 rounded-xl border border-teal-400/20">
            <p className="text-slate-300 text-center">
              <strong className="text-white">Fair Use:</strong> These terms are designed to be fair and transparent. 
              We believe in building trust through clear communication about how our service works.
            </p>
          </div>
        </div>
      </motion.main>
    </div>
  );
};

export default TermsOfService;
